import ExportArtifactOption from "../ExportArtifactOption";
import { styled } from "@linaria/react";
import * as io from "io-ts";
import cx from "classnames";
import React from "react";
import createIOSortType from "../../../../../lib/createIOSortType";
import {
  numberValidation,
  strictlyPositiveNumberValidation,
} from "../../../formValidationFunctions";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useSortedData from "../../../../../lib/hooks/useSortedData";
import t from "../../../../../i18n";
import type { SortableValue } from "diagnostics-lib/comparator";
import { color, fonts, opacityElementDisabled } from "@ui";
import Card from "@ui/Card";
import useConfirmModal from "@ui/Modal/useConfirmModal";
import RadioButton, { RADIO_BUTTON_GAP } from "@ui/RadioButton";
import Switch from "@ui/Switch";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import TableEditableCell, { TableInputControl } from "@ui/Table/EditableCell";
import TableForm from "@ui/Table/Form";
import { WarningComponent } from "../Simulation/SimulationStyle";
import { useQuantities } from "../../../../../api/useQuantities";
import ObservableActions from "./Observables/ObservableActions";
import type { Setup } from "../../../../../api/useSetups";
import { usePatch, usePut } from "../../../../../api/useCall";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import useDirtyForm from "@ui/Table/useDirtyForm";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import BlankStateV2 from "@ui/BlankStateV2";
import { useSetupActiveAnalysisCount } from "../../../../../api/useActiveAnalysesStream";
import { DeleteBinLine, Link, More2Fill, UploadLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import { css } from "@linaria/core";
import formatNumber from "@ui/i18n/formatNumber";
import {
  TableCardWithoutBorder,
  TableHeaderNoPaddingT,
} from "@ui/Table/_styles/TableCardWithoutBorder";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import ButtonIconV2 from "@ui/ButtonIconV2";
import usePrevious from "@ui/lib/hooks/usePrevious";
import { spacings } from "@ui/Theme";

const Name = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-left: ${RADIO_BUTTON_GAP};
  margin-right: auto;
`;

const DisabledText = styled.p`
  opacity: ${opacityElementDisabled};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;
  height: 100%;
  align-items: center;
`;

const ObservableRadioButton = styled(RadioButton)`
  display: flex;
  align-items: center;
  height: 40px;

  input {
    border-width: 2px;
  }
`;

const StyledActions = styled(ObservableActions)`
  display: none;
  margin-left: auto;

  tr:hover & {
    display: flex;
  }
`;

export type ObservableModal = "import" | "delete";

interface ObservablesInfoProps {
  setup: Setup;
  projectId: string;
  openModal: (name: ObservableModal) => void;
}

type Observable = {
  name: string;
  unit: string | null;
  description: string | null;
  quantityId: string;
  id: string;
  sigma: number;
  enabled: boolean;
  filter: { min: number | null; max: number | null; maxStd: number | null } | null;
};

const columns = ["name", "sigma", "offset", "unit", "description"] as const;
type Column = (typeof columns)[number];

const SORT_SELECTORS: {
  [key in Column]: (
    u: Observable & { offset: number | undefined },
  ) => SortableValue | undefined | null;
} = {
  name: u => u.name,
  unit: u => u.unit,
  sigma: u => u.sigma,
  offset: u => u.offset,
  description: () => null,
};

const Wrapper = styled.div`
  max-height: 150px;
`;

const MainText = styled.div`
  margin-bottom: ${spacings.m};
  font: ${fonts.body1};
`;

const LabelHint = css`
  max-width: 200px;
`;

const LabelHintContent = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    display: block;
  }
`;

interface ConfirmToggleObservableProps {
  enable: boolean;
  quantityName: string;
  analysesImpactedCount: number;
}
const ConfirmToggleObservable = ({
  enable,
  quantityName,
  analysesImpactedCount,
}: ConfirmToggleObservableProps) => (
  <Wrapper>
    <MainText>{t("setup.diagnosis.observable.confirm.toggle.body", enable, quantityName)}</MainText>
    <WarningComponent impactedAnalysisCount={analysesImpactedCount} />
  </Wrapper>
);

interface ConfirmEditObservableProps {
  quantityName: string;
  analysesImpactedCount: number;
}

const ConfirmEditObservable = ({
  quantityName,
  analysesImpactedCount,
}: ConfirmEditObservableProps) => (
  <Wrapper>
    <MainText>{t("setup.diagnosis.observable.confirm.edit.body", quantityName)}</MainText>
    <WarningComponent impactedAnalysisCount={analysesImpactedCount} />
  </Wrapper>
);

const computeRowKey = (observable: Observable) => `observable-id-${observable.id}`;

const useObservableSort = createGlobalState({
  storage: "session",
  key: "observableSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

const useObservableSearch = createGlobalState({
  storage: "session",
  key: "observableSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const useDisplayDisabled = createGlobalState({
  storage: "session",
  key: "displayDisabledObservable",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const getLabel = (column: Column) => {
  switch (column) {
    case "name":
      return t("setup.diagnosis.observable.table.column.quantity");
    case "description":
      return t("setup.diagnosis.observable.table.column.description");
    case "unit":
      return t("setup.diagnosis.observable.table.column.unit");
    case "sigma":
      return t("setup.diagnosis.observable.table.column.sigma");
    case "offset":
      return t("setup.simulation.table.offset");
    default:
      throw new Error(column satisfies never);
  }
};

const getLabelHint = (column: Exclude<Column, "name" | "sigma">) => {
  switch (column) {
    case "description":
      return t("setup.diagnosis.observable.table.column.description.hint");
    case "unit":
      return t("setup.diagnosis.observable.table.column.unit.hint");
    case "offset":
      return t("setup.diagnosis.observable.table.column.offset.hint");
    default:
      throw new Error(column satisfies never);
  }
};

const FormSignature = io.partial({
  sigma: io.string,
  offset: io.string,
});

type FormSignature = io.TypeOf<typeof FormSignature>;

type EditableObservable = Observable & {
  offset: number;
  edited?: boolean;
};

export const ObservablesInfo = ({ setup, projectId, openModal }: ObservablesInfoProps) => {
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const setupId = setup.id;
  const activeAnalysisCount = useSetupActiveAnalysisCount(projectId, setupId);
  const inputs = setup.diagnosisInputs;
  const simulationOutputs = setup.simulationOutputs;
  const quantities = useQuantities(projectId);
  const [editedDataKey, setEditedDataKey] = React.useState<string>();
  const [dirty, setDirty] = useDirtyForm<string>();

  const [offsets, setOffsets] = React.useState<Record<string, number>>({});
  const [sigmas, setSigmas] = React.useState<Record<string, number>>({});
  const previousOffsets = usePrevious(offsets);
  const previousSigmas = usePrevious(sigmas);

  const observables: EditableObservable[] = React.useMemo(
    () =>
      inputs
        .filter(input => quantities.some(quantity => quantity.id === input.quantityId))
        .map(input => {
          const quantityFound = quantities.find(q => input.quantityId === q.id);
          if (!quantityFound) throw new Error("it should not happen");

          const simulationOutputFound = simulationOutputs.find(
            o => input.quantityId === o.quantityId,
          );
          return {
            ...input,
            sigma: sigmas[input.id] ?? input.sigma,
            name: quantityFound.name,
            description: quantityFound.description,
            unit: quantityFound.unit,
            offset: offsets[input.id] ?? simulationOutputFound?.offsetExpected ?? 0,
            offsetOutputId: simulationOutputFound?.id,
            edited: computeRowKey(input as Observable) === editedDataKey,
            filter: {
              min: quantityFound.minMetrology,
              max: quantityFound.maxMetrology,
              maxStd: quantityFound.maxStdMetrology,
            },
          };
        }),
    [editedDataKey, inputs, offsets, quantities, sigmas, simulationOutputs],
  );

  const [search, setSearch] = useObservableSearch({ scope: setup.id });
  const [sort, setSort] = useObservableSort({ scope: setup.id });
  const [displayDisabled, setDisplayDisabled] = useDisplayDisabled({ scope: setup.id });
  const observablesEnabled = React.useMemo(() => observables.filter(o => o.enabled), [observables]);

  const observableToShow = React.useMemo(
    () => (displayDisabled ? observables : observablesEnabled),
    [displayDisabled, observables, observablesEnabled],
  );
  const allObservablesNotActivated = React.useMemo(
    () => observables.some(o => !o.enabled),
    [observables],
  );

  const confirm = useConfirmModal();
  const updateObservable = usePut(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/inputs/:inputId(uuid)",
  );
  const updateOutputEndpoint = usePatch(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/output/:outputId(uuid)",
  );

  const sortedDatas = useSortedData(observableToShow, SORT_SELECTORS[sort.column], sort.direction);

  const [filteredDatas, filteringInProgress] = useFilteredDatas(
    sortedDatas,
    search,
    obs => obs.name,
  );
  const handleCheckboxChange = React.useCallback(() => {
    setDisplayDisabled(!displayDisabled);
  }, [setDisplayDisabled, displayDisabled]);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const headerLeft = (
    <>
      <SearchInputV2
        data-cy="search-project"
        placeholder={t("commons.placeholder.search")}
        onChange={handleSearchChange}
        value={search}
      />
      {allObservablesNotActivated && (
        <Switch checked={displayDisabled} onChange={handleCheckboxChange}>
          {t("setup.diagnosis.observable.show_disabled")}
        </Switch>
      )}
    </>
  );

  const openImportModal = React.useCallback(() => {
    openModal("import");
  }, [openModal]);
  const openDeleteModal = React.useCallback(() => openModal("delete"), [openModal]);
  const headerRight = canUserEditSetup && (
    <Menu
      arrowWidth={0}
      //   gap={16}
      placement="bottom-end"
      overlay={
        <>
          <OptionGroup>
            <ExportArtifactOption projectId={projectId} setupId={setup.id} section="observable" />
            <Option closeOnClick onClick={openImportModal}>
              <>
                <UploadLine color={color.carbon800} />
                {t("commons.message_card.replace")}
              </>
            </Option>
            <Option closeOnClick onClick={openDeleteModal}>
              <DeleteBinLine color={color.carbon800} />
              {t("commons.message_card.delete")}
            </Option>
          </OptionGroup>
        </>
      }
    >
      <ButtonIconV2>
        <More2Fill size="m" />
      </ButtonIconV2>
    </Menu>
  );

  const toggleObservable = React.useCallback(
    (observable: Observable) => async () => {
      const quantity = quantities.find(q => q.id === observable.quantityId);
      if (quantity === undefined) throw new Error("should not happen");
      const isConfirm = await confirm({
        title: t("setup.diagnosis.observable.confirm.toggle.title", !observable.enabled),
        body: (
          <ConfirmToggleObservable
            enable={observable.enabled}
            quantityName={quantity.name}
            analysesImpactedCount={activeAnalysisCount}
          />
        ),
        okButtonName: t("action.confirm"),
      });

      if (isConfirm) {
        await updateObservable({
          routeParams: { projectId, setupId, inputId: observable.id },
          body: { sigma: observable.sigma, enabled: !observable.enabled },
        });
      }
    },
    [confirm, activeAnalysisCount, projectId, quantities, setupId, updateObservable],
  );

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<EditableObservable> } = {
      name: {
        label: getLabel("name"),
        render: obs => (
          <>
            {canUserEditSetup && (
              <ObservableRadioButton
                checked={obs.enabled}
                readOnly
                onClick={toggleObservable(obs)}
              />
            )}
            <Name title={obs.name}>
              {obs.enabled ? obs.name : <DisabledText>{obs.name}</DisabledText>}
            </Name>
            {!obs.edited && canUserEditSetup ? (
              <StyledActions
                onEdit={() => setEditedDataKey(computeRowKey(obs))}
                className={HIDE_ON_EXPORT}
              />
            ) : null}
          </>
        ),
        sticky: true,
        sortable: true,
        maxWidth: "300px",
        minWidth: "130px",
      },
      sigma: {
        label: getLabel("sigma"),
        render: obs => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="sigma"
            dataKey={computeRowKey(obs)}
            edited={obs.edited}
            setEditedDataKey={setEditedDataKey}
            showEmptyMark
            renderControl={props => (
              <TableInputControl
                {...props}
                validations={[strictlyPositiveNumberValidation]}
                validateOnBlur
                validateOnFocus
                defaultValue={obs.sigma.toString()}
                setDirty={setDirty}
                placeholder={getLabel("sigma")}
                type="number"
              />
            )}
          >
            {obs.enabled ? (
              formatNumber(obs.sigma)
            ) : (
              <DisabledText>{formatNumber(obs.sigma)}</DisabledText>
            )}
          </TableEditableCell>
        ),
        sortable: true,
        alignment: "left",
        maxWidth: "120px",
        verticalAlignmentCenter: false,
      },
      offset: {
        label: (
          <div className={LabelHintContent}>
            {getLabel("offset")}
            <TooltipV2 overlay={getLabelHint("offset")} className={LabelHint}>
              <div>
                <Link size="s" />
              </div>
            </TooltipV2>
          </div>
        ),
        render: obs => (
          <TableEditableCell<Column>
            enabled={canUserEditSetup}
            column="offset"
            dataKey={computeRowKey(obs)}
            edited={obs.edited}
            setEditedDataKey={setEditedDataKey}
            showEmptyMark
            renderControl={props => (
              <TableInputControl
                {...props}
                validations={[numberValidation]}
                defaultValue={String(obs.offset)}
                setDirty={setDirty}
                placeholder={t("setup.simulation.table.offset")}
                type="number"
              />
            )}
          >
            {obs.enabled ? (
              obs.offset ? (
                formatNumber(obs.offset, { sign: true })
              ) : undefined
            ) : (
              <DisabledText>
                {obs.offset ? formatNumber(obs.offset, { sign: true }) : undefined}
              </DisabledText>
            )}
          </TableEditableCell>
        ),
        sortable: true,
        alignment: "left",
        maxWidth: "120px",
        verticalSeparator: true,
        verticalAlignmentCenter: false,
      },
      unit: {
        label: (
          <div className={LabelHintContent}>
            {getLabel("unit")}
            <TooltipV2 overlay={getLabelHint("unit")} className={LabelHint}>
              <div>
                <Link size="s" />
              </div>
            </TooltipV2>
          </div>
        ),
        render: obs => (obs.enabled ? obs.unit : <DisabledText>{obs.unit}</DisabledText>),
        sortable: true,
        maxWidth: "120px",
      },
      description: {
        label: (
          <div className={LabelHintContent}>
            {getLabel("description")}
            <TooltipV2 overlay={getLabelHint("description")} className={LabelHint}>
              <div>
                <Link size="s" />
              </div>
            </TooltipV2>
          </div>
        ),
        render: obs =>
          obs.enabled ? obs.description : <DisabledText>{obs.description}</DisabledText>,
        sortable: false,
      },
    };
    return _;
  }, [setDirty, canUserEditSetup, toggleObservable]);
  const handleFormClose = React.useCallback(() => setEditedDataKey(undefined), []);
  const findDataByDataKey = React.useCallback(
    (key: string) => sortedDatas.find(observable => computeRowKey(observable) === key),
    [sortedDatas],
  );
  const cancelEditing = React.useCallback(() => {
    handleFormClose();
  }, [handleFormClose]);

  const handleEdit = React.useCallback(
    async (values: FormSignature, observable: Observable) => {
      const quantity = quantities.find(q => q.id === observable.quantityId);
      const simulationOutput = simulationOutputs.find(o => o.quantityId === observable.quantityId);

      if (quantity === undefined || simulationOutput === undefined)
        throw new Error("should not happen");

      const isConfirm = await confirm({
        title: t("setup.diagnosis.observable.confirm.edit.title"),
        body: (
          <ConfirmEditObservable
            quantityName={quantity.name}
            analysesImpactedCount={activeAnalysisCount}
          />
        ),
        okButtonName: t("action.confirm"),
      });

      if (isConfirm) {
        setOffsets(offsets => ({ ...offsets, [observable.id]: Number(values.offset) }));
        setSigmas(sigmas => ({ ...sigmas, [observable.id]: Number(values.sigma) }));
        Promise.all([
          updateOutputEndpoint({
            body: {
              offsetExpected: Number(values.offset),
            },
            routeParams: {
              projectId,
              setupId,
              outputId: simulationOutput.id,
            },
          }),
          updateObservable({
            body: {
              sigma: Number(values.sigma),
              enabled: observable.enabled,
            },
            routeParams: { projectId, setupId, inputId: observable.id },
          }),
        ]).catch(() => {
          setOffsets(previousOffsets ?? {});
          setSigmas(previousSigmas ?? {});
        });
      }
    },
    [
      quantities,
      simulationOutputs,
      confirm,
      activeAnalysisCount,
      updateOutputEndpoint,
      projectId,
      setupId,
      updateObservable,
      previousOffsets,
      previousSigmas,
    ],
  );

  if (observables.length === 0)
    return <EmptyState canEditSetup={canUserEditSetup} openModal={openModal} />;
  // t("setup.diagnosis.observable.title")
  return (
    <TableCard
      title=""
      headerLeft={headerLeft}
      headerRight={headerRight}
      className={cx(TableCardWithoutBorder, TableHeaderNoPaddingT)}
    >
      <TableForm
        onClose={cancelEditing}
        findDataByDataKey={findDataByDataKey}
        editedDataKey={editedDataKey}
        signature={FormSignature}
        onSubmit={handleEdit}
        dirty={dirty}
        setDirty={setDirty}
      >
        <Table
          datas={filteredDatas}
          loading={filteringInProgress}
          stickyHeader
          metadata={metadata}
          computeRowKey={computeRowKey}
          sort={sort}
          onSort={setSort}
        />
      </TableForm>
    </TableCard>
  );
};

interface EmptyStateProps {
  openModal: (modal: ObservableModal) => void;
  canEditSetup: boolean;
}

const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 25px 20px;
`;

function EmptyState({ openModal, canEditSetup }: EmptyStateProps) {
  return (
    <Card>
      <StyledBlankStateV2
        title={t("setup.diagnosis.observable.blank.title")}
        subtitle={canEditSetup ? t("setup.diagnosis.observable.blank.subtitle") : null}
        onClick={canEditSetup ? () => openModal("import") : undefined}
        buttonLabel={canEditSetup ? t("setup.diagnosis.observable.blank.button") : undefined}
      />
    </Card>
  );
}
